'use client';

import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

interface LeaderboardEntry {
  user_id: string;
  total_points: number;
  level: number;
  full_name: string;
  badges: string[];
  places_count: number;
}

interface Badge {
  badge_type: string;
  awarded_at: string;
}

const BADGE_META: Record<string, { icon: string; label: string; color: string; description: string }> = {
  first_discovery: { icon: '🌟', label: 'First Discovery', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30', description: 'Submitted your first place' },
  explorer: { icon: '🧭', label: 'Explorer', color: 'text-blue-400 bg-blue-400/10 border-blue-400/30', description: 'Earned 100+ explorer points' },
  trailblazer: { icon: '🔥', label: 'Trailblazer', color: 'text-orange-400 bg-orange-400/10 border-orange-400/30', description: 'Earned 500+ explorer points' },
  top_contributor: { icon: '🏆', label: 'Top Contributor', color: 'text-gold bg-gold/10 border-gold/30', description: 'Earned 1000+ explorer points' },
  verified_explorer: { icon: '✅', label: 'Verified Explorer', color: 'text-green-400 bg-green-400/10 border-green-400/30', description: 'Earned 2000+ explorer points' },
  photographer: { icon: '📸', label: 'Photographer', color: 'text-pink-400 bg-pink-400/10 border-pink-400/30', description: 'Uploaded 10+ photos' },
  navigator: { icon: '🗺️', label: 'Navigator', color: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30', description: 'Added GPS to 5+ places' },
  community_hero: { icon: '💪', label: 'Community Hero', color: 'text-purple-400 bg-purple-400/10 border-purple-400/30', description: 'Helped 50+ community members' },
  hidden_gem_finder: { icon: '💎', label: 'Hidden Gem Finder', color: 'text-indigo-400 bg-indigo-400/10 border-indigo-400/30', description: 'Discovered 5+ hidden places' },
};

function getLevelTitle(level: number): string {
  if (level >= 15) return 'Legendary Explorer';
  if (level >= 10) return 'Master Explorer';
  if (level >= 7) return 'Senior Explorer';
  if (level >= 5) return 'Experienced Traveller';
  if (level >= 3) return 'Adventurer';
  if (level >= 2) return 'Wanderer';
  return 'Newcomer';
}

function getLevelColor(level: number): string {
  if (level >= 15) return 'text-yellow-300';
  if (level >= 10) return 'text-gold';
  if (level >= 7) return 'text-orange-400';
  if (level >= 5) return 'text-blue-400';
  if (level >= 3) return 'text-green-400';
  return 'text-snow/60';
}

export default function LeaderboardPage() {
  const { user } = useAuth();
  const supabase = createClient();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [myStats, setMyStats] = useState<{ points: number; level: number; badges: Badge[]; rank: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'leaderboard' | 'badges'>('leaderboard');

  useEffect(() => {
    fetchLeaderboard();
    if (user) fetchMyStats();
  }, [user]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    const { data: points } = await supabase
      .from('explorer_points')
      .select('user_id, total_points, level')
      .order('total_points', { ascending: false })
      .limit(50);

    if (!points || points.length === 0) { setLoading(false); return; }

    const userIds = points.map((p: any) => p.user_id);

    const [usersRes, badgesRes, placesRes] = await Promise.all([
      supabase.from('users').select('id, full_name').in('id', userIds),
      supabase.from('user_badges').select('user_id, badge_type').in('user_id', userIds),
      supabase.from('discovered_places').select('submitted_by').eq('status', 'published').in('submitted_by', userIds),
    ]);

    const usersMap: Record<string, string> = {};
    (usersRes.data || []).forEach((u: any) => { usersMap[u.id] = u.full_name; });

    const badgesMap: Record<string, string[]> = {};
    (badgesRes.data || []).forEach((b: any) => {
      if (!badgesMap[b.user_id]) badgesMap[b.user_id] = [];
      badgesMap[b.user_id].push(b.badge_type);
    });

    const placesMap: Record<string, number> = {};
    (placesRes.data || []).forEach((p: any) => {
      placesMap[p.submitted_by] = (placesMap[p.submitted_by] || 0) + 1;
    });

    const entries: LeaderboardEntry[] = points.map((p: any) => ({
      user_id: p.user_id,
      total_points: p.total_points,
      level: p.level,
      full_name: usersMap[p.user_id] || 'Explorer',
      badges: badgesMap[p.user_id] || [],
      places_count: placesMap[p.user_id] || 0,
    }));

    setLeaderboard(entries);
    setLoading(false);
  };

  const fetchMyStats = async () => {
    if (!user) return;
    const [pointsRes, badgesRes] = await Promise.all([
      supabase.from('explorer_points').select('total_points, level').eq('user_id', user.id).maybeSingle(),
      supabase.from('user_badges').select('badge_type, awarded_at').eq('user_id', user.id),
    ]);

    const { data: allPoints } = await supabase
      .from('explorer_points')
      .select('user_id')
      .order('total_points', { ascending: false });

    const rank = (allPoints || []).findIndex((p: any) => p.user_id === user.id) + 1;

    setMyStats({
      points: pointsRes.data?.total_points || 0,
      level: pointsRes.data?.level || 1,
      badges: badgesRes.data || [],
      rank: rank || 0,
    });
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  return (
    <main className="min-h-screen bg-navy">
      <Header />
      <div className="pt-20">
        {/* Hero */}
        <div className="bg-gradient-to-b from-navy-mid to-navy py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 rounded-full px-4 py-1.5 text-gold text-sm mb-4">
              <span>🏆</span> Explorer Rewards
            </div>
            <h1 className="text-snow font-bold text-3xl md:text-4xl mb-3">Community Leaderboard</h1>
            <p className="text-snow/60 text-base max-w-xl mx-auto">
              Earn explorer points by discovering hidden places, sharing photos, and helping the community.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* My Stats Card */}
          {user && myStats && (
            <div className="yn-card rounded-2xl p-5 mb-8 border border-gold/20">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-snow/50 text-xs mb-1">Your Explorer Status</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center text-xl font-bold text-gold">
                      {myStats.level}
                    </div>
                    <div>
                      <p className={`font-bold text-base ${getLevelColor(myStats.level)}`}>{getLevelTitle(myStats.level)}</p>
                      <p className="text-snow/60 text-sm">{myStats.points.toLocaleString()} explorer points</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 text-center">
                  <div>
                    <p className="text-gold font-bold text-xl">{myStats.rank > 0 ? `#${myStats.rank}` : '—'}</p>
                    <p className="text-snow/40 text-xs">Rank</p>
                  </div>
                  <div>
                    <p className="text-gold font-bold text-xl">{myStats.badges.length}</p>
                    <p className="text-snow/40 text-xs">Badges</p>
                  </div>
                </div>
              </div>

              {/* Progress to next level */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-snow/40 mb-1">
                  <span>Level {myStats.level}</span>
                  <span>Level {myStats.level + 1}</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-gold to-yellow-300 rounded-full transition-all"
                    style={{ width: `${Math.min(100, ((myStats.points - (myStats.level - 1) ** 2 * 100) / ((myStats.level ** 2 * 100) - (myStats.level - 1) ** 2 * 100)) * 100)}%` }}
                  />
                </div>
              </div>

              {/* My badges */}
              {myStats.badges.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {myStats.badges.map(b => {
                    const meta = BADGE_META[b.badge_type];
                    if (!meta) return null;
                    return (
                      <span key={b.badge_type} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${meta.color}`} title={meta.description}>
                        {meta.icon} {meta.label}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {(['leaderboard', 'badges'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors capitalize ${
                  activeTab === tab ? 'bg-gold/20 text-gold border border-gold/30' : 'bg-white/5 text-snow/60 hover:text-snow border border-white/10'
                }`}
              >
                {tab === 'leaderboard' ? '🏆 Leaderboard' : '🎖️ All Badges'}
              </button>
            ))}
          </div>

          {activeTab === 'leaderboard' && (
            <div className="space-y-3">
              {loading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="yn-card rounded-xl p-4 animate-pulse h-16" />
                ))
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-5xl mb-4">🏆</div>
                  <h3 className="text-snow font-bold text-lg mb-2">No explorers yet</h3>
                  <p className="text-snow/40 text-sm mb-6">Be the first to discover a hidden place and earn points!</p>
                  <Link href="/discover/submit" className="btn-gold px-6 py-3 rounded-xl text-sm">Discover a Place</Link>
                </div>
              ) : (
                leaderboard.map((entry, idx) => (
                  <div
                    key={entry.user_id}
                    className={`yn-card rounded-xl p-4 flex items-center gap-4 ${idx < 3 ? 'border-gold/20' : ''}`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center text-lg font-bold shrink-0 ${
                      idx === 0 ? 'text-yellow-300' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-amber-600' : 'text-snow/40'
                    }`}>
                      {getRankIcon(idx + 1)}
                    </div>

                    <div className="w-10 h-10 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center text-sm font-bold text-gold shrink-0">
                      {entry.full_name.charAt(0).toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-snow font-semibold text-sm">{entry.full_name}</p>
                        {entry.badges.includes('verified_explorer') && (
                          <span className="text-xs text-green-400 bg-green-400/10 border border-green-400/20 px-1.5 py-0.5 rounded-full">✅ Verified</span>
                        )}
                        {entry.badges.includes('top_contributor') && !entry.badges.includes('verified_explorer') && (
                          <span className="text-xs text-gold bg-gold/10 border border-gold/20 px-1.5 py-0.5 rounded-full">🏆 Top Contributor</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className={`text-xs ${getLevelColor(entry.level)}`}>Lv.{entry.level} {getLevelTitle(entry.level)}</span>
                        <span className="text-snow/30 text-xs">•</span>
                        <span className="text-snow/40 text-xs">🗺️ {entry.places_count} places</span>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-gold font-bold text-base">{entry.total_points.toLocaleString()}</p>
                      <p className="text-snow/40 text-xs">points</p>
                    </div>

                    {entry.badges.length > 0 && (
                      <div className="hidden sm:flex gap-1 shrink-0">
                        {entry.badges.slice(0, 3).map(b => (
                          <span key={b} title={BADGE_META[b]?.label || b} className="text-base">{BADGE_META[b]?.icon || '🎖️'}</span>
                        ))}
                        {entry.badges.length > 3 && <span className="text-snow/40 text-xs self-center">+{entry.badges.length - 3}</span>}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'badges' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(BADGE_META).map(([key, meta]) => (
                <div key={key} className="yn-card rounded-xl p-4 flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl border flex items-center justify-center text-2xl shrink-0 ${meta.color}`}>
                    {meta.icon}
                  </div>
                  <div>
                    <p className="text-snow font-semibold text-sm">{meta.label}</p>
                    <p className="text-snow/50 text-xs mt-0.5">{meta.description}</p>
                    {myStats?.badges.some(b => b.badge_type === key) && (
                      <span className="inline-block mt-2 text-xs text-green-400 bg-green-400/10 border border-green-400/20 px-2 py-0.5 rounded-full">✓ Earned</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* How to earn points */}
          <div className="mt-10 yn-card rounded-2xl p-6">
            <h3 className="text-snow font-bold text-base mb-4">💡 How to Earn Explorer Points</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { action: 'Submit a place for review', points: '+10 pts' },
                { action: 'Place gets published', points: '+50 pts' },
                { action: 'Someone likes your place', points: '+2 pts' },
                { action: 'Leave a comment', points: '+3 pts' },
                { action: 'Confirm a place still exists', points: '+5 pts' },
              ].map(item => (
                <div key={item.action} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-2.5">
                  <span className="text-snow/70 text-sm">{item.action}</span>
                  <span className="text-gold font-semibold text-sm">{item.points}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
